/**
 * Flitship Toast Component — Light System
 * 
 * Calm, minimal, non-aggressive notifications
 * Following Flitship branding: reliable, human, calm, globally minded
 * 
 * Toast variants:
 * - Success: green bg #eafaf0, border #b6efc2, text #2a8a3e
 * - Error: red bg #fce8e8, border #f7c1c1, text #a32d2d
 * - Info: blue bg #eef2fd, border #c5d8fa, text #1a3acc
 * 
 * Position: bottom-center, 80px margin (above bottom nav)
 * Duration: 3000ms auto-dismiss
 */

import { Toaster as Sonner, toast as sonnerToast } from "sonner";
import { CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * Toast variant configurations
 */
const TOAST_VARIANTS = {
  success: {
    bg: '#eafaf0',
    border: '#b6efc2',
    text: '#2a8a3e',
    icon: CheckCircle,
  },
  error: {
    bg: '#fce8e8',
    border: '#f7c1c1',
    text: '#a32d2d',
    icon: AlertCircle,
  },
  info: {
    bg: '#eef2fd',
    border: '#c5d8fa',
    text: '#1a3acc',
    icon: Info,
  },
  default: {
    bg: '#faf6f2',
    border: '#e8e4e0',
    text: '#1f1f1f',
    icon: Info,
  },
};

/**
 * Toast rendering component — Light system
 */
const ToastContent = ({ message, type = 'default' }) => {
  const variant = TOAST_VARIANTS[type] || TOAST_VARIANTS.default;
  const Icon = variant.icon;

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg max-w-[90vw] sm:max-w-sm animate-in fade-in slide-in-from-bottom-2 duration-300"
      style={{
        backgroundColor: variant.bg,
        border: `1px solid ${variant.border}`,
      }}
    >
      {/* Icon */}
      <Icon 
        className="w-5 h-5 flex-shrink-0" 
        style={{ color: variant.text }}
        strokeWidth={2}
      />
      
      {/* Message */}
      <p 
        className="text-[13px] font-semibold leading-snug"
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          color: variant.text,
        }}
      >
        {message}
      </p>
    </div>
  );
};

/**
 * Flitship-branded Toaster
 * - Light system with colored variants
 * - Positioned bottom-center, 80px above bottom nav
 * - Brief duration (3 seconds)
 * - Smooth animations
 */
const FlitshipToaster = () => {
  return (
    <Sonner
      position="bottom-center"
      toastOptions={{
        unstyled: true,
        duration: 3000,
      }}
      gap={8}
      offset={80} // Above bottom navigation
      closeButton={false}
      visibleToasts={2}
    />
  );
};

/**
 * Internal notify function
 */
const showToast = (message, type = 'default', duration = 3000) => {
  sonnerToast.custom(
    () => <ToastContent message={message} type={type} />,
    { duration }
  );
};

/**
 * Toast API — Compatible with existing sonner toast API
 * 
 * Usage:
 *   toast.success('Trajet publié avec succès !')
 *   toast.error('Une erreur est survenue')
 *   toast.info('Nouveau colis disponible')
 *   toast('Message simple')
 */
const toast = (message) => showToast(message, 'default');

toast.success = (message) => showToast(message, 'success');
toast.error = (message) => showToast(message, 'error');
toast.info = (message) => showToast(message, 'info');
toast.message = (message) => showToast(message, 'default');

/**
 * Pre-configured Flitship notifications
 */
const flitshipNotify = {
  // Generic
  show: (message) => showToast(message),
  success: (message) => showToast(message, 'success'),
  info: (message) => showToast(message, 'info'),
  error: (message) => showToast(message, 'error'),
  
  // Mission lifecycle
  newMatch: () => showToast("Un nouveau colis correspond à votre trajet.", 'info'),
  missionCreated: () => showToast("Votre mission a été créée.", 'success'),
  paymentSecured: () => showToast("Votre paiement est sécurisé.", 'success'),
  packageInTransit: () => showToast("Le colis est en cours de transport.", 'info'),
  packageDelivered: () => showToast("Le colis a été livré.", 'success'),
  paymentReleased: () => showToast("Le paiement a été envoyé.", 'success'),
  
  // Communication
  messageReceived: () => showToast("Vous avez reçu un nouveau message.", 'info'),
  
  // Progress
  missionProgress: () => showToast("Votre mission progresse.", 'info'),
  
  // Trip
  tripPublished: () => showToast("Trajet publié avec succès !", 'success'),
  tripCancelled: () => showToast("Trajet annulé.", 'info'),
  
  // Request
  requestSent: () => showToast("Demande envoyée avec succès !", 'success'),
  requestAccepted: () => showToast("Demande acceptée !", 'success'),
  requestRefused: () => showToast("Demande refusée.", 'info'),
};

export { FlitshipToaster, toast, flitshipNotify };
